import { Component, HostListener, Input, Output, EventEmitter, NgZone, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { Subscription } from 'rxjs';
import { FroelingData, FroelingError, FroelingHistoryPoint, FroelingSetting, FroelingSettingChange, FroelingSettingsGroup, FroelingSettingsSnapshot, PelletDayStat, PelletUsageBucket, PelletUsageSummary } from '../version.service';
import { updatedAgo as fmtAgo } from '../shared/format.utils';
import { CHART_RANGES, CHART_RANGE_MS, ChartRange, chartPalette, coveringRange, timeTooltipTitle, lineLegendLabels, dragZoom, setpointColor, timeAxisTick, circuitColor, exhaustColor, alignedTimeTicks } from '../shared/chart.utils';
import { DeepLinkService, LinkWindow } from '../shared/deep-link.service';
import {
  SettingHint, areaFill, boilerStateText, burnerLegend, groupIcon, hydraulicsSeries, recentDailyKg, settingChangeParts, settingGroupLabel,
  settingHint, settingHints, settingLabel, settingValue, settingWindowLines, storeDaysLeft,
} from '../shared/froeling.utils';

const HOUR_MS = 3600 * 1000;
const DAY_MS  = 24 * HOUR_MS;

/** Deep-link view paths of the card popups; each popup's chart uses its path as its link id. */
const VIEWS = {
  boiler:       'heizung/boiler',
  buffer:       'heizung/buffer',
  water:        'heizung/water',
  solarThermal: 'heizung/solarThermal',
  outdoor:      'heizung/outdoor',
  pump:         'heizung/pump',
  pellet:       'heizung/pellet',
  hydraulics:   'heizung/hydraulics',
  settings:     'heizung/settings',
} as const;
/** The 📈 all-series modal (AppComponent): not one of this component's popups. */
const CHART_MODAL_VIEW = 'heizung/chart';

/** Options of an expanded chart before its data arrives: its link id, so the deep-link plugin
 *  follows the chart from its creation (a popup may open before the range data is loaded), and
 *  the container's height — Chart.js' default 2:1 aspect ratio would stay after the real options
 *  arrive (no resize follows), e.g. a pellet chart 183 px high in a tapped-open phone sheet. */
function linkOnlyOptions(id: string): any {
  return { responsive: true, maintainAspectRatio: false, plugins: { deepLink: { id } } };
}

/**
 * Scale option for the yMinSpan plugin: the axis' minimum span, 10 × the values' precision (a
 * quantisation step takes at most a tenth of the height). Given absolutely, not as `resolution`:
 * the plugin's step detection would take an on/off series' 0 → 100 % (or a bar chart's 0 → 30 kg)
 * as its "precision" and stretch the axis tenfold.
 */
function yMinSpan(span: number): any {
  return { minSpan: span };
}

/** Phone width (the popups are full-screen sheets there): compact chart legends, ticks and tooltips. */
function isPhone(): boolean {
  return window.innerWidth < 576;
}
/** Phone popup charts: smaller legend items and tooltip, so the chart keeps most of the sheet. */
const PHONE_LEGEND = { padding: 6, font: { size: 11 } };
const PHONE_TOOLTIP = { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 6, boxWidth: 8, boxHeight: 8 };

/** Local YYYY-MM-DD of a date (the dashboard's users are in Europe/Berlin, like the backend's days). */
function localIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'app-heizung',
  templateUrl: './heizung.component.html',
  styles: [`
    .hz-card-chart { position: relative; height: 90px; margin-top: 6px; }
    .hz-target { font-size: .72rem; }
    /* card popup: range buttons (+ the pellet figures) above the chart */
    .hz-pop-bar { display: flex; flex-wrap: wrap; align-items: center; gap: .25rem .5rem; margin-bottom: .25rem; }
    .hz-range { display: flex; align-items: center; gap: .5rem; margin-left: auto; }
    .hz-sheet { container-type: inline-size; }
    .hz-pop-chart { position: relative; aspect-ratio: 16 / 9; }
    .hz-pellet-chart { min-height: 230px; }
    .hz-pellet-stats { display: flex; flex-wrap: wrap; column-gap: .9rem; row-gap: .1rem; font-size: .72rem; }
    .range-spin { display: none; }
    /* the Hydraulik card's chart fills the card (as tall as its row) */
    .hz-hyd-chart { height: auto; flex: 1 1 120px; min-height: 120px; }
    .hz-meta-row { display: flex; flex-wrap: wrap; column-gap: .5rem; }
    /* Fröling settings popup: label left, value right; hints neutral */
    .hz-set { font-size: .82rem; }
    .hz-set h6 { font-size: .8rem; text-transform: uppercase; letter-spacing: .05em; margin: .9rem 0 .3rem; color: var(--bs-secondary-color); }
    .hz-set-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: .75rem; padding: .22rem 0;
                  border-bottom: 1px solid var(--bs-border-color-translucent); }
    .hz-set-row .v { font-weight: 600; text-align: right; white-space: nowrap; }
    .hz-set-hint { grid-column: 1 / -1; font-size: .75rem; color: var(--bs-secondary-color); margin-top: .1rem; }
    .hz-set-hints { background: var(--bs-tertiary-bg); border-radius: .4rem; padding: .5rem .7rem; font-size: .78rem; }
    .hz-set-hints ul { margin: .25rem 0 0; padding-left: 1.1rem; }
    .hz-set-log { display: grid; grid-template-columns: auto minmax(0, 1fr); column-gap: .6rem; padding: .2rem 0;
                  border-bottom: 1px solid var(--bs-border-color-translucent); font-size: .78rem; }
    /* centred card: 16:9, but no taller than the screen leaves (a phone turned sideways, a short
       window) — full width either way (a max-height on the aspect-ratio box would narrow it) */
    @supports (width: 1cqw) and (height: 1dvh) {
      @media (min-width: 576px) {
        .hz-pop-chart { aspect-ratio: auto; height: max(200px, min(56.25cqw, calc(100dvh - 160px))); }
        .hz-pellet-chart { height: max(230px, min(56.25cqw, calc(100dvh - 210px))); }
      }
    }

    @media (max-width: 575.98px) {
      /* card on a phone (full width, dash-chart-col): name left, value at the end of its row, the other lines below */
      .hz-text {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        column-gap: .75rem;
        align-items: baseline;
      }
      .hz-text > * { grid-column: 1 / -1; }
      .hz-text > .hz-label { grid-row: 1; grid-column: 1; }
      .hz-text > .hz-value { grid-row: 1; grid-column: 2; }
      .hz-value.card-value, .hz-value .card-value { font-size: 1.25rem !important; line-height: 1.25; }
      .hz-text .dash-meta, .hz-target { font-size: .75rem; }
      .hz-card-chart { margin-top: 8px; }
      /* popup sheet: the range control spans the width, the figures below it, the chart fills the rest */
      .hz-sheet { height: 100%; }
      .hz-pop-bar { margin-bottom: .5rem; }
      .hz-range { flex: 1 0 100%; margin-left: 0; }
      .hz-range > .dash-seg { flex: 1 1 auto; width: auto; }
      .hz-pellet-stats { order: 1; font-size: .75rem; }
      .hz-pop-chart { aspect-ratio: auto; height: auto; }
      .hz-set-row { grid-template-columns: minmax(0, 1fr) auto; }
      .hz-set-row .v { white-space: normal; max-width: 45vw; }
      /* a loading range: its (pressed) button shows a spinner in place of the label, the button keeps its size */
      .range-busy > .range-label { opacity: 0; }
      .range-spin { display: block; position: absolute; inset: 0; margin: auto; width: 1rem; height: 1rem; border-width: .15em; }
    }
  `],
})
export class HeizungComponent implements OnChanges, OnDestroy {
  // The charts' labels, axis titles, ticks and tooltips are translated in TS: rebuild on a language switch.
  private langSub: Subscription;
  private linkSub: Subscription;

  constructor(private translate: TranslateService, private link: DeepLinkService, private zone: NgZone) {
    this.langSub = translate.onLangChange.subscribe(() => {
      this.rebuild();
      if (this.pelletBars.length) this.buildPelletUsageChart();
    });
    // The popups follow the link by themselves (popup-card linkId); the range is ours.
    this.linkSub = link.external$.subscribe(() => this.applyLink());
    this.applyLink();
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.linkSub.unsubscribe();
  }

  /** Phone width: the popup charts get a compact legend / tooltip, fewer ticks and no axis titles. */
  private phone = isPhone();

  /** Crossing the phone breakpoint rebuilds the charts' options (a linked zoom window is restored by the plugin). */
  @HostListener('window:resize')
  onResize(): void {
    if (isPhone() === this.phone) return;
    this.phone = isPhone();
    this.rebuild();
    if (this.pelletBars.length) this.buildPelletUsageChart();
  }

  /** View paths of the popups, for the template's [linkId] bindings. */
  readonly views = VIEWS;

  /** View path of a heating circuit's popup (= its chart's link id). */
  hcView(name: string): string {
    return 'heizung/hc/' + name;
  }

  /** Heating-circuit cards by name: each Fröling frame (every 60 s) brings new objects, and an
   *  identity-tracked *ngFor would rebuild the open popup, its overlay and its chart every minute. */
  readonly trackHc = (_: number, hc: { name: string }) => hc.name;

  /** Ist colour of the i-th heating circuit (its lines and its card value). */
  hcColor(i: number): string {
    return circuitColor(this.darkMode);   // every circuit alike (its own chart)
  }

  /** Set-point (Soll) colour: the dashed Soll lines and the card's "/ x° Soll". */
  get setpointColor(): string {
    return setpointColor(this.darkMode);
  }

  /** The open popup of this tab (a view path, = its chart's link id), or null. */
  private get openPopup(): string | null {
    const v = this.link.view;
    return v && v.startsWith('heizung/') && v !== CHART_MODAL_VIEW ? v : null;
  }

  /**
   * Apply the link's range (on creation and when the browser navigates). A popup's linked zoom
   * window older than that range loads the range that holds it — and the link says so.
   */
  private applyLink(): void {
    const linked = this.link.range('heizung') ?? 'day';
    const popup = this.openPopup;
    const zoom = popup ? this.link.zoom(popup) : null;
    const range = popup === VIEWS.pellet ? this.pelletCoveringRange(linked, zoom) : coveringRange(linked, zoom);
    if (range !== linked) this.link.set(this.link.chartKey('heizung', 'range'), range);
    if (range !== this.range) this.setRange(range, true);
  }

  /** As coveringRange(), for the pellet chart's whole-day windows. */
  private pelletCoveringRange(range: ChartRange, zoom: LinkWindow | null): ChartRange {
    if (!zoom) return range;
    const order: ChartRange[] = ['day', 'week', 'month', 'year'];
    return order.slice(order.indexOf(range)).find(r => this.pelletWindow(r).from <= zoom.from) ?? 'year';
  }

  /** The chart has a zoom window (drag / pinch, or from the link): its ↩ Reset button. */
  isZoomed(id: string): boolean {
    return !!this.link.zoom(id);
  }

  /** Undo the drag / pinch zoom of an expanded chart (the plugin drops the window from the link). */
  resetZoom(chart: BaseChartDirective | null | undefined): void {
    (chart?.chart as any)?.resetZoom?.();
  }

  /** Zoom events run outside Angular: show / hide the Reset button right away. */
  private readonly zoomChanged = () => this.zone.run(() => {});

  @Input() froeling: FroelingData | null = null;
  @Input() froelingHistory: FroelingHistoryPoint[] = [];
  @Input() froelingFlash = false;
  @Input() liveDotOn = true;
  @Input() froelingUpdatedAt: number | null = null;
  @Input() nowTick = Date.now();
  @Input() darkMode = false;
  @Input() pelletStats: PelletDayStat[] = [];
  /** Live pellet figures (current burn rate, kg today / last 24 h) from the backend's pellet ledger. */
  @Input() pelletUsage: PelletUsageSummary | null = null;
  /** The Fröling's settings and their change log (read-only). */
  @Input() froelingSettings: FroelingSettingsSnapshot | null = null;
  @Output() openHeizungChart = new EventEmitter<void>();

  hydChartData: ChartData<'line'> = { datasets: [] };
  hydChartOptions: ChartConfiguration<'line'>['options'] = {};
  hydExpandedData: ChartData<'line'> = { datasets: [] };
  hydExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.hydraulics);

  // ── live values of the cards ─────────────────────────────────────────────

  /** The boiler state in the UI language (the controller's own text for codes without a translation). */
  get boilerStateLabelLive(): string {
    const f = this.froeling;
    return f ? boilerStateText(f.boilerState, f.boilerStateText, k => this.translate.instant(k)) : '';
  }

  /** kg per day over the last 14 complete days (the pellet store's days left). */
  get recentKgPerDay(): number | null {
    return recentDailyKg(this.pelletStats);
  }

  /** Days the pellet store lasts at the recent consumption (null: unknown / hardly any use). */
  get storeDays(): number | null {
    return storeDaysLeft(this.froeling?.storeRemainingT, this.recentKgPerDay);
  }

  /** The circulation return: its °C, or "no sensor" when it reads 0 °C constantly. */
  get circReturnLabel(): string | null {
    const f = this.froeling;
    if (f?.circulationReturnTempC == null) return null;
    return f.circulationReturnNoSensor ? this.translate.instant('HEIZUNG_NO_SENSOR')
      : `${Math.round(f.circulationReturnTempC)}°`;
  }

  // ── settings view ────────────────────────────────────────────────────────

  readonly groupIcon = groupIcon;

  private readonly tr = (key: string, params?: Record<string, unknown>): string => this.translate.instant(key, params);

  /** A setting's short label in the UI language (froeling.utils: shared with the Analyse board's settings widget). */
  settingLabel(g: FroelingSettingsGroup, s: FroelingSetting): string {
    return settingLabel(g, s, this.tr);
  }

  /** A setting's value: the code's text, a number with its unit. */
  settingValue(s: FroelingSetting): string {
    return settingValue(s, this.locale);
  }

  settingHint(g: FroelingSettingsGroup, s: FroelingSetting): SettingHint | null {
    return settingHint(g, s, this.froeling);
  }

  /**
   * Every hint of the snapshot (the box at the top of the view) — a field, recomputed when the settings or the
   * reading change (a getter would hand *ngFor new objects on every change detection: its rows rebuilt twice a second).
   */
  settingHintList: { group: FroelingSettingsGroup; hint: SettingHint }[] = [];

  private updateSettingHints(): void {
    const out = settingHints(this.froelingSettings, this.froeling);
    // keep the list (and its rows) while nothing changed
    const key = (l: typeof out) => JSON.stringify(l.map(h => [h.group.componentId, h.hint.key, h.hint.params]));
    if (key(out) !== key(this.settingHintList)) this.settingHintList = out;
  }

  readonly trackHint = (_: number, h: { group: FroelingSettingsGroup; hint: SettingHint }): string => h.group.componentId + '|' + h.hint.key;

  /** A component's name in the UI language ("Hot-water tank 01" for the controller's "Boiler 01"), by its type. */
  groupLabel(g: { type: string; name: string } | undefined, fallback = ''): string {
    return settingGroupLabel(g, this.tr, fallback);
  }

  /** The group of a change-log entry (null: no longer in the snapshot). */
  changeGroup(componentId: string): FroelingSettingsGroup | undefined {
    return this.froelingSettings?.components.find(x => x.componentId === componentId);
  }

  /** A component's time windows, days with equal windows merged. */
  windowLines(g: FroelingSettingsGroup): string[] {
    return settingWindowLines(g, this.tr);
  }

  /** A change of the log: "Kessel-Soll: 80 → 70 °C". */
  changeText(c: FroelingSettingChange): string {
    const p = settingChangeParts(c, this.froelingSettings, this.tr, this.locale);
    return `${p.setting}: ${p.from} → ${p.to}`;
  }

  fmtDateTime(ts: number): string {
    return new Date(ts).toLocaleString(this.locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  /** Fröling errors still pending (or of unknown state) — the red banner. */
  get activeErrors(): FroelingError[] {
    return (this.froeling?.errors ?? []).filter(e => e.active !== false);
  }

  /** Fröling errors that are no longer pending, only not yet acknowledged — a neutral note. */
  get pastErrors(): FroelingError[] {
    return (this.froeling?.errors ?? []).filter(e => e.active === false);
  }

  /** An error's ISO time in the UI locale (the raw text if it does not parse). */
  errTime(iso: string): string {
    const t = Date.parse(iso);
    return isNaN(t) ? iso : new Date(t).toLocaleString(this.locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  /** The settings card: the newest change's time, or null. */
  get lastSettingChange(): number | null {
    return this.froelingSettings?.changes?.[0]?.ts ?? null;
  }

  boilerChartData: ChartData<'line'> = { datasets: [] };
  boilerChartOptions: ChartConfiguration<'line'>['options'] = {};
  boilerExpandedData: ChartData<'line'> = { datasets: [] };
  boilerExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.boiler);

  bufferChartData: ChartData<'line'> = { datasets: [] };
  bufferChartOptions: ChartConfiguration<'line'>['options'] = {};
  bufferExpandedData: ChartData<'line'> = { datasets: [] };
  bufferExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.buffer);

  waterChartData: ChartData<'line'> = { datasets: [] };
  waterChartOptions: ChartConfiguration<'line'>['options'] = {};
  waterExpandedData: ChartData<'line'> = { datasets: [] };
  waterExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.water);

  solarThermalChartData: ChartData<'line'> = { datasets: [] };
  solarThermalChartOptions: ChartConfiguration<'line'>['options'] = {};
  solarThermalExpandedData: ChartData<'line'> = { datasets: [] };
  solarThermalExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.solarThermal);

  outdoorChartData: ChartData<'line'> = { datasets: [] };
  outdoorChartOptions: ChartConfiguration<'line'>['options'] = {};
  outdoorExpandedData: ChartData<'line'> = { datasets: [] };
  outdoorExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.outdoor);

  /** Card: kg of the last 7 calendar days (0-filled, today lighter). */
  pelletChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  pelletChartOptions: ChartConfiguration<'bar'>['options'] = {};
  /** Overlay: one chart, bars = kg per hour / day, line = burn rate kg/h (mixed bar + line). */
  pelletUsageData: ChartData<'bar'> = { datasets: [] };
  pelletUsageOptions: ChartConfiguration<'bar'>['options'] = linkOnlyOptions(VIEWS.pellet);
  pelletUsageLoading = false;
  pelletRangeKg: number | null = null;
  pelletRangeBurnH: number | null = null;
  pelletRangeAvgRate: number | null = null;
  private pelletBars: PelletUsageBucket[] = [];
  private pelletLine: PelletUsageBucket[] = [];
  private pelletUsageSeq = 0;
  private pelletUsageLoadedAt = 0;
  private pelletOverlayOpen = false;
  /** The overlay chart is drag/pinch-zoomed (or shows a linked window): the periodic reload waits until it is reset. */
  get pelletZoomed(): boolean { return this.isZoomed(VIEWS.pellet); }

  pumpChartData: ChartData<'line'> = { datasets: [] };
  pumpChartOptions: ChartConfiguration<'line'>['options'] = {};
  pumpExpandedData: ChartData<'line'> = { datasets: [] };
  pumpExpandedOptions: ChartConfiguration<'line'>['options'] = linkOnlyOptions(VIEWS.pump);

  hcChartData: { [name: string]: ChartData<'line'> } = {};
  hcChartOptions: ChartConfiguration<'line'>['options'] = {};
  hcExpandedData: { [name: string]: ChartData<'line'> } = {};
  /** Per circuit: each expanded chart has its own link id. */
  hcExpandedOptions: { [name: string]: ChartConfiguration<'line'>['options'] } = {};

  // Per-tab time range: 'day' uses the live 24h history; longer ranges fetch the
  // downsampled long-term archive and drive every heizung chart. Linked as "heizung.range".
  range: ChartRange = 'day';
  rangeLoading = false;
  froelingRangeHistory: FroelingHistoryPoint[] = [];
  /** Range buttons; labels are i18n keys (RANGE_DAY …). */
  readonly chartRanges = CHART_RANGES;

  get displayFroeling(): FroelingHistoryPoint[] {
    return this.range === 'day' ? this.froelingHistory : this.froelingRangeHistory;
  }
  /** Translated label of the current range (re-read every change detection, so it follows a language switch). */
  get rangeLabel(): string {
    const key = this.chartRanges.find(r => r.key === this.range)?.label;
    return key ? this.translate.instant(key) : '';
  }

  private rangeSeq = 0;

  /** Select the range (a button; `fromLink`: applied from the deep link, which is not written back). */
  setRange(range: ChartRange, fromLink = false): void {
    if (!fromLink) {
      // a new range shows its whole window: the open popup's zoom goes
      const popup = this.openPopup;
      if (popup && range !== this.range) this.link.setZoom(popup, null);
      this.link.setRange('heizung', range);
    }
    this.range = range;
    if (this.pelletOverlayOpen) this.loadPelletUsage();
    const seq = ++this.rangeSeq;   // a slower earlier response must not overwrite a newer range
    if (range === 'day') { this.rangeLoading = false; this.rebuild(); return; }
    const to   = Date.now();
    const from = to - CHART_RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=froeling&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => {
        if (seq !== this.rangeSeq) return;
        this.froelingRangeHistory = pts ?? []; this.rangeLoading = false; this.rebuild();
      })
      .catch(() => { if (seq === this.rangeSeq) this.rangeLoading = false; });
  }

  // ── Pellets ──────────────────────────────────────────────────────────────

  /** Pellets burnt today (kg) — the backend's ledger, else today's row of the daily list. */
  get pelletTodayKg(): number {
    if (this.pelletUsage) return this.pelletUsage.todayKg;
    const today = localIsoDate(new Date());
    return this.pelletStats.find(s => s.date === today)?.consumptionKg ?? 0;
  }

  /**
   * kg in the last 24 h, always over 24 h whatever chart range is selected. From the backend's
   * ledger; before its first frame from the live history: the sum of the positive counter steps
   * (a failed read of 0 or a backward step never counts).
   */
  get pellet24hKg(): number {
    if (this.pelletUsage) return this.pelletUsage.last24hKg;
    let kg = 0, prev = NaN;
    for (const p of this.froelingHistory) {
      const c = p.pelletConsumptionKg;
      if (!(c > 0)) continue;
      if (c > prev && c - prev < 50) kg += c - prev;
      prev = c;
    }
    return kg;
  }

  /** Undo a drag/pinch zoom of the pellet overlay chart (and catch up on the data that arrived meanwhile). */
  resetPelletZoom(chart: BaseChartDirective): void {
    this.resetZoom(chart);
    this.loadPelletUsage();
  }

  /** The pellet overlay opened / closed: its chart data is loaded only while it is open. */
  onPelletOverlay(open: boolean): void {
    this.pelletOverlayOpen = open;
    if (open) this.loadPelletUsage();
  }

  /**
   * The overlay's time window: the last 24 whole hours plus the current one for Tag; whole local
   * days for the longer ranges (today included up to its end) — so the running hour's / today's
   * bar gets its full slot and is not cut off at "now".
   */
  private pelletWindow(range: ChartRange = this.range): { from: number; to: number } {
    const now = Date.now();
    if (range === 'day') {
      // floor + 1 h (not ceil): at exactly hh:00 the new hour still gets its slot
      const to = Math.floor(now / HOUR_MS) * HOUR_MS + HOUR_MS;
      return { from: to - CHART_RANGE_MS.day - HOUR_MS, to };
    }
    const days = Math.round(CHART_RANGE_MS[range] / DAY_MS);
    const d = new Date(now);
    const from = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (days - 1)).getTime();
    const to = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
    return { from, to };
  }

  /** Bars + line of the overlay from /api/pellets/usage for the selected range. */
  private loadPelletUsage(): void {
    const seq = ++this.pelletUsageSeq;
    const short = this.range === 'day';
    const { from, to } = this.pelletWindow();
    const url = (bucket: string) => `/api/pellets/usage?from=${from}&to=${to}&bucket=${bucket}`;
    const get = (bucket: string): Promise<PelletUsageBucket[]> =>
      fetch(url(bucket)).then(r => r.ok ? r.json() : []).then(v => Array.isArray(v) ? v : []);
    this.pelletUsageLoading = true;
    Promise.all(short ? [get('hour'), get('10min')] : [get('day')])
      .then(([bars, line]) => {
        if (seq !== this.pelletUsageSeq) return;
        this.pelletUsageLoadedAt = Date.now();
        this.pelletUsageLoading = false;
        this.pelletBars = bars;
        this.pelletLine = short ? (line ?? []) : bars;
        this.buildPelletUsageChart();
      })
      .catch(() => { if (seq === this.pelletUsageSeq) this.pelletUsageLoading = false; });
  }

  /** Age of a frame; a frame newer than the last 1 s tick is "0s" (not "vor -1s"). */
  updatedAgo(ts: number | null): string { return fmtAgo(ts, Math.max(this.nowTick, ts ?? 0)); }

  hcLabel(name: string): string {
    const match = name.match(/(\d+)/);
    return match ? this.translate.instant('HEIZKREIS') + ' ' + match[1] : name;
  }

  pumpLabel(state: string | undefined): string {
    if (!state) return '';
    const s = state.toLowerCase().trim();
    if (s === 'ein' || s === 'on' || s === 'an' || s === '1' || s === 'true') return this.translate.instant('HEIZUNG_ON');
    if (s === 'aus' || s === 'off' || s === '0' || s === 'false') return this.translate.instant('HEIZUNG_OFF');
    return state;
  }

  private pumpToNumber(state: string | undefined): number {
    if (!state) return 0;
    const s = state.toLowerCase().trim();
    if (s === 'ein' || s === 'on' || s === 'an' || s === '1' || s === 'true') return 1;
    return 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only rebuild charts when the underlying data/theme changes — NOT on every
    // liveDotOn (600ms) / nowTick (1000ms) tick, which would otherwise re-map the
    // whole history and reassign every chart object continuously.
    // While the pellet overlay is open its chart follows the live data (a new kg step / the
    // per-minute pellet frame), at most every minute (Tag) or 5 minutes (longer ranges).
    if ((changes['pelletUsage'] || changes['pelletStats']) && this.pelletOverlayOpen && !this.pelletZoomed
        && Date.now() - this.pelletUsageLoadedAt > (this.range === 'day' ? 60_000 : 300_000)) {
      this.loadPelletUsage();
    }
    if (changes['froelingSettings'] || changes['froeling']) this.updateSettingHints();
    if (!changes['froelingHistory'] && !changes['froeling']
        && !changes['darkMode'] && !changes['pelletStats']) return;
    if (changes['darkMode'] && this.pelletBars.length) this.buildPelletUsageChart();
    this.rebuild();
  }

  private rebuild(): void {
    this.buildPelletBarChart();
    const pts = this.displayFroeling;
    if (pts.length < 2) return;
    const { textColor, gridColor } = chartPalette(this.darkMode);
    // x window spans the selected range (now-windowMs … now) so date labels show
    // across a Woche/Monat even when the archive is still sparse. 'Tag' = relative ticks.
    const xMax = Date.now();
    const xMin = xMax - CHART_RANGE_MS[this.range];
    const range = this.range;
    const t = (k: string, p?: any): string => this.translate.instant(k, p);
    const locale = this.locale;
    // 'Tag' over the whole window: ticks every 12 h (card) / 6 h (popup) back from now, each labelled
    // (Chart.js' own ticks seldom hit "–12h", so most charts showed only "–24h … Jetzt").
    const dayTicks = (expanded: boolean): number[] => {
      const step = (expanded ? 6 : 12) * HOUR_MS, t: number[] = [];
      for (let v = xMax; v >= xMin - 1; v -= step) t.unshift(v);
      return t;
    };
    const nowLabel = t('HEIZUNG_TICK_NOW');
    const dayTick = (val: any): string => {
      const h = Math.round((xMax - +val) / HOUR_MS);
      return h <= 0 ? nowLabel : `–${h}h`;
    };
    const fullTick = range === 'day' ? dayTick : timeAxisTick(xMin, xMax);
    // A drag / pinch or linked zoom window (dragZoom has no pan, so a narrower scale is a zoom):
    // its ticks and labels follow the visible window, not the full range — else a 2 h window of
    // 'Tag' gets no "Jetzt / –12h" label at all, and a 1-day window of 'Woche' only "19.10 19.10 …".
    const isZoomedScale = (s: any) => s.max - s.min < (xMax - xMin) * 0.99;
    const xTick = function (this: any, val: any): string | null {
      if (!isZoomedScale(this)) return fullTick(val);
      if (this.max - this.min < 3 * HOUR_MS) {   // under ~2 whole hours: HH:mm of Chart.js' own ticks
        const d = new Date(+val);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      }
      return timeAxisTick(this.min, this.max)(val);
    };
    // In a Woche / Monat / Jahr window the time alone is ambiguous: the tooltip names the day too.
    const tooltipTitle = range === 'day' ? timeTooltipTitle : (items: any[]): string => {
      const ts = items[0]?.parsed.x;
      if (ts == null) return '';
      return new Date(ts).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    // A popup chart on a phone (a full-screen sheet): compact legend / tooltip, fewer ticks at
    // 10 px, no axis titles (the unit is in the ticks, the name in the legend and the sheet's title).
    const phone = this.phone;
    const tickFont = (expanded: boolean) => ({ size: expanded && !phone ? 11 : 10 });
    const popTooltip = (expanded: boolean) => expanded && phone ? PHONE_TOOLTIP : {};

    // `linkId`: an expanded chart's deep-link id (its popup's view path) — legend selection and
    // zoom window go into the link.
    const buildOptions = (expanded: boolean, yLabel: string, yUnit: string, linkId?: string): ChartConfiguration<'line'>['options'] => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: expanded
          ? { display: true, labels: { ...lineLegendLabels(textColor), ...(phone ? PHONE_LEGEND : {}) } }
          : { display: false },
        tooltip: {
          ...popTooltip(expanded),
          callbacks: {
            title: tooltipTitle,
            label: (item) => ` ${(item.parsed.y ?? 0).toFixed(1)} ${yUnit}`,
          },
        },
        ...(expanded ? { zoom: dragZoom(this.zoomChanged) } : {}),
        ...(linkId ? linkOnlyOptions(linkId).plugins : {}),
      },
      scales: {
        x: {
          display: true,
          type: 'linear',
          min: xMin,
          max: xMax,
          // zoomed: whole hours / noons / month starts of the visible window (as the pellet chart),
          // thinned by the default autoSkip; otherwise 'Tag' every 12 h / 6 h (dayTicks), the longer
          // ranges Chart.js' own ticks, limited by maxTicksLimit
          afterBuildTicks: (scale: any) => {
            if (!isZoomedScale(scale)) {
              if (range === 'day') scale.ticks = dayTicks(expanded).map(value => ({ value }));
              return;
            }
            const t = alignedTimeTicks(scale.min, scale.max);
            if (t.length >= 2) scale.ticks = t.map(value => ({ value }));
          },
          ticks: {
            color: textColor,
            maxTicksLimit: expanded ? (phone ? 4 : 8) : 3,
            font: tickFont(expanded),
            maxRotation: 0,
            callback: xTick,
          },
          grid: { display: expanded, color: gridColor },
        },
        y: {
          // The Fröling reports whole °C / %: the axis spans at least 10 of them, so a value flipping
          // between 45 and 46 °C stays a calm line (also for the archive's bucket means, which have
          // 0.01 steps but no more real precision).
          ...yMinSpan(10),
          display: true,
          ticks: {
            color: textColor,
            maxTicksLimit: expanded ? (phone ? 5 : 6) : 4,
            // whole-number labels: a narrow axis would otherwise repeat "45 °C, 45 °C"
            precision: 0,
            font: tickFont(expanded),
            callback: (val) => `${(val as number).toFixed(0)} ${yUnit}`,
          },
          grid: { color: gridColor, display: expanded },
          title: { display: expanded && !phone, text: yLabel, color: textColor, font: { size: 11 } },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line: { borderWidth: expanded ? 2 : 1.5, tension: 0.4 },
      },
    });

    const onLabel = t('HEIZUNG_ON'), offLabel = t('HEIZUNG_OFF');
    const buildDualAxisOptions = (expanded: boolean, yLabel: string, yUnit: string, y1Label: string, linkId?: string): ChartConfiguration<'line'>['options'] => {
      const base = buildOptions(expanded, yLabel, yUnit, linkId)!;
      return {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            ...popTooltip(expanded),
            callbacks: {
              title: tooltipTitle,
              label: (item) => {
                if ((item.dataset as any).yAxisID === 'y1') {
                  return ` ${item.dataset.label}: ${item.parsed.y ? onLabel : offLabel}`;
                }
                return ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} ${yUnit}`;
              },
            },
          },
        },
        scales: {
          ...base.scales,
          y1: {
            position: 'right' as const,
            min: -0.1,
            max: 1.1,
            display: expanded,
            afterBuildTicks: (scale: any) => { scale.ticks = [{ value: 0 }, { value: 1 }]; },   // Aus / Ein only
            ticks: {
              color: textColor,
              font: { size: 10 },
              maxTicksLimit: 3,
              callback: (val: any) => val === 1 ? onLabel : val === 0 ? offLabel : '',
            },
            grid: { drawOnChartArea: false },
            title: { display: expanded && !phone, text: y1Label, color: textColor, font: { size: 11 } },
          },
        },
      };
    };

    // Standard line dataset from a point accessor — dedupes the repeated
    // borderColor / transparent-bg / no-fill boilerplate across the charts below.
    // `linkKey`: the dataset's stable name in the deep link (labels are not).
    const lineDs = (linkKey: string, label: string, accessor: (p: FroelingHistoryPoint) => number,
                    color: string, opts: any = {}): any => ({
      linkKey,
      label,
      data: pts.map(p => ({ x: p.ts, y: accessor(p) })),
      borderColor: color,
      backgroundColor: 'transparent',
      fill: false,
      ...opts,
    });

    // Boiler chart: temp + exhaust (solid, in a colour of its own: dashed orange means Soll)
    const exhaust = exhaustColor(this.darkMode);
    const boilerData = (expanded: boolean): ChartData<'line'> => ({
      datasets: [
        lineDs('boilerTempC', t('HEIZUNG_KESSEL'), p => p.boilerTempC, '#dc3545', { borderWidth: expanded ? 2 : 1.5 }),
        ...(expanded ? [lineDs('exhaustTempC', t('HEIZUNG_ABGAS'), p => p.exhaustTempC, exhaust, { borderWidth: 2 })] : []),
      ],
    });
    this.boilerChartData = boilerData(false);
    this.boilerChartOptions = buildOptions(false, t('HEIZUNG_AXIS_TEMP'), '°C');
    this.boilerExpandedData = boilerData(true);
    this.boilerExpandedOptions = buildOptions(true, t('HEIZUNG_AXIS_TEMP'), '°C', VIEWS.boiler);

    // Buffer chart: top + bottom temperature (°C, left axis) + charge level (%, right axis 0–100)
    const bufferData = (expanded: boolean): ChartData<'line'> => ({
      datasets: [
        lineDs('bufferTopTempC', t('HEIZUNG_BUFFER_TOP'), p => p.bufferTopTempC, '#dc3545', { borderWidth: expanded ? 2 : 1.5 }),
        ...(expanded ? [lineDs('bufferBottomTempC', t('HEIZUNG_BUFFER_BOTTOM'), p => p.bufferBottomTempC, '#0d6efd', { borderWidth: 2 })] : []),
        lineDs('bufferChargePct', t('HEIZUNG_BUFFER_CHARGE'), p => p.bufferChargePct, '#ffc107', {
          yAxisID: 'y1', borderWidth: expanded ? 1.5 : 1,
          backgroundColor: 'rgba(255,193,7,0.15)', fill: 'origin', tension: 0.2,
        }),
      ],
    });
    const bufferOptions = (expanded: boolean, yLabel: string): ChartConfiguration<'line'>['options'] => {
      const base = buildOptions(expanded, yLabel, '°C', expanded ? VIEWS.buffer : undefined)!;
      return {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            ...popTooltip(expanded),
            callbacks: {
              title: tooltipTitle,
              label: (item) => (item.dataset as any).yAxisID === 'y1'
                ? ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(0)} %`
                : ` ${item.dataset.label}: ${(item.parsed.y ?? 0).toFixed(1)} °C`,
            },
          },
        },
        scales: {
          ...base.scales,
          y1: {
            position: 'right' as const,
            min: 0,
            max: 100,
            display: expanded,
            ticks: { color: textColor, font: { size: 10 }, maxTicksLimit: 5, callback: (val: any) => `${val} %` },
            grid: { drawOnChartArea: false },
            title: { display: expanded && !phone, text: t('HEIZUNG_AXIS_CHARGE'), color: textColor, font: { size: 11 } },
          },
        },
      };
    };
    this.bufferChartData = bufferData(false);
    this.bufferChartOptions = bufferOptions(false, t('HEIZUNG_AXIS_TEMP'));
    this.bufferExpandedData = bufferData(true);
    this.bufferExpandedOptions = bufferOptions(true, t('HEIZUNG_AXIS_BUFFER'));

    // Hot water chart
    const waterData = (): ChartData<'line'> => ({ datasets: [lineDs('hotWaterTempC', t('HEIZUNG_WARMWASSER'), p => p.hotWaterTempC, '#0dcaf0')] });
    this.waterChartData = waterData();
    this.waterChartOptions = buildOptions(false, t('HEIZUNG_AXIS_TEMP'), '°C');
    this.waterExpandedData = waterData();
    this.waterExpandedOptions = buildOptions(true, t('HEIZUNG_AXIS_WATER'), '°C', VIEWS.water);

    // Solar thermal chart
    const solarData = (): ChartData<'line'> => ({ datasets: [lineDs('solarThermalTempC', t('HEIZUNG_COLLECTOR'), p => p.solarThermalTempC, '#ffc107')] });
    this.solarThermalChartData = solarData();
    this.solarThermalChartOptions = buildOptions(false, t('HEIZUNG_AXIS_TEMP'), '°C');
    this.solarThermalExpandedData = solarData();
    this.solarThermalExpandedOptions = buildOptions(true, t('HEIZUNG_AXIS_SOLAR'), '°C', VIEWS.solarThermal);

    // Outdoor temperature chart
    const outdoorData = (): ChartData<'line'> => ({ datasets: [lineDs('outdoorTempC', t('HEIZUNG_OUTDOOR_SHORT'), p => p.outdoorTempC, '#6c757d')] });
    this.outdoorChartData = outdoorData();
    this.outdoorChartOptions = buildOptions(false, t('HEIZUNG_AXIS_TEMP'), '°C');
    this.outdoorExpandedData = outdoorData();
    this.outdoorExpandedOptions = buildOptions(true, t('HEIZUNG_AXIS_OUTDOOR'), '°C', VIEWS.outdoor);

    // Circulation pump chart
    const hasPumpData = pts.some(p => (p.circulationPumpPct ?? 0) > 0);
    if (hasPumpData) {
      const pumpData = (): ChartData<'line'> => ({
        datasets: [lineDs('circulationPumpPct', t('HEIZUNG_PUMP_PCT'), p => p.circulationPumpPct ?? 0, '#0d6efd',
          { backgroundColor: 'rgba(13,110,253,0.15)', fill: true })],
      });
      this.pumpChartData = pumpData();
      this.pumpChartOptions = buildOptions(false, t('HEIZUNG_AXIS_PUMP_SPEED'), '%');
      this.pumpExpandedData = pumpData();
      this.pumpExpandedOptions = buildOptions(true, t('HEIZUNG_AXIS_PUMP_SPEED'), '%', VIEWS.pump);
    }

    // Hydraulik: boiler, buffer, the circuits' flow Ist / Soll and the DHW tank on °C; below them, in a strip of their
    // own (a stacked 0–100 % axis, so they never cover the temperatures), the buffer and DHW pumps; the burner shaded
    // over the full height while it burns — where the heat goes, on one time axis
    const hydData = (compact: boolean): ChartData<'line'> => ({
      datasets: hydraulicsSeries(pts, t, this.darkMode, n => this.hcLabel(n), compact).map(s => ({
        linkKey: s.key,
        label: s.label,
        data: pts.map(p => ({ x: p.ts, y: s.value(p) })),
        borderColor: s.color,
        backgroundColor: s.area ? areaFill(s.color, this.darkMode, s.key === 'burner') : 'transparent',
        fill: s.area ? 'origin' : false,
        borderWidth: s.key === 'burner' ? 0.75 : s.area ? 1.25 : s.soll ? 1.5 : compact ? 1.5 : 2,
        ...(s.dash ? { borderDash: s.dash } : {}),
        ...(s.area ? { stepped: 'before' as const, tension: 0 } : {}),
        ...(s.soll ? { spanGaps: false } : {}),
        yAxisID: s.key === 'burner' ? 'y2' : s.axis === 'pct' ? 'yp' : 'y',
        order: s.area ? 2 : 1,
        // the burner's legend swatch: a shaded box (a line would read as the boiler's of the same hue)
        ...(s.key === 'burner' ? burnerLegend(s.color, this.darkMode) : {}),
      } as any)),
    });
    const hydOptions = (expanded: boolean): ChartConfiguration<'line'>['options'] => {
      const base = buildOptions(expanded, t('HEIZUNG_AXIS_TEMP'), '°C', expanded ? VIEWS.hydraulics : undefined)!;
      const onLabel = t('HEIZUNG_ON'), offLabel = t('HEIZUNG_OFF');
      // a downsampled bucket: the share of its time the burner burned
      const burner = (y: number) => y >= 0.995 ? onLabel : y <= 0.005 ? offLabel : `${onLabel} ${Math.round(y * 100)} %`;
      const basePad = (base.layout as any)?.padding ?? {};
      return {
        ...base,
        // the expanded chart's legend reaches the right edge: room above it for the zoom reset button (top right)
        ...(expanded ? { layout: { padding: { ...basePad, top: phone ? 34 : 28 } } } : {}),
        plugins: {
          ...base.plugins,
          legend: expanded
            ? { display: true, labels: { ...lineLegendLabels(textColor), ...(phone ? PHONE_LEGEND : {}) } }
            : { display: false },
          tooltip: {
            ...popTooltip(expanded),
            callbacks: {
              title: tooltipTitle,
              label: (item) => {
                const ds = item.dataset as any;
                const y = item.parsed.y;
                if (y == null) return '';
                if (ds.yAxisID === 'y2') return ` ${ds.label}: ${burner(y)}`;
                return ds.yAxisID === 'yp' ? ` ${ds.label}: ${y.toFixed(0)} %` : ` ${ds.label}: ${y.toFixed(1)} °C`;
              },
            },
          },
        },
        scales: {
          x: (base.scales as any).x,
          // the pumps: a 0–100 % strip below the temperatures (expanded only; the card has no pumps) — of
          // stacked axes the first one is drawn at the bottom
          ...(expanded ? {
            yp: {
              position: 'left' as const,
              stack: 'hyd',
              stackWeight: 1,
              // a fixed margin, not `offset` (autoSkip would drop the 100 % label of a short strip); above 100 % a
              // gap, so its label stays clear of the temperatures' lowest one
              min: -8,
              max: 135,
              afterBuildTicks: (scale: any) => { scale.ticks = [0, 100].map(value => ({ value })); },
              ticks: { color: textColor, font: tickFont(true), autoSkip: false, callback: (val: any) => `${val} %` },
              grid: { color: gridColor },
              title: { display: !phone, text: t('HYD_AXIS_PUMPS'), color: textColor, font: { size: 11 } },
            },
          } : {}),
          y: { ...(base.scales as any).y, stack: 'hyd', stackWeight: 4 },
          // the burner: the full height, no axis of its own
          y2: { min: 0, max: 1, display: false },
        },
      };
    };
    this.hydChartData = hydData(true);
    this.hydChartOptions = hydOptions(false);
    this.hydExpandedData = hydData(false);
    this.hydExpandedOptions = hydOptions(true);

    // Heating circuits charts — Ist in the circuit's colour, Soll dashed in the set-point colour,
    // pump state (expanded only) grey on the right axis
    const circuits = this.froeling?.heatingCircuits ?? [];
    if (circuits.length > 0) {
      const hcCompact = buildOptions(false, t('HEIZUNG_AXIS_TEMP'), '°C')!;
      // With the set-point line the small chart holds two lines: name each value in the tooltip.
      this.hcChartOptions = {
        ...hcCompact,
        plugins: {
          ...hcCompact.plugins,
          tooltip: {
            callbacks: {
              title: tooltipTitle,
              label: (item) => (item.chart.data.datasets.length > 1 ? ` ${item.dataset.label}:` : '')
                + ` ${(item.parsed.y ?? 0).toFixed(1)} °C`,
            },
          },
        },
      };
      const sollColor = setpointColor(this.darkMode);
      const pumpColor = this.darkMode ? '#adb5bd' : '#6c757d';
      const pumpFill  = this.darkMode ? 'rgba(173,181,189,0.12)' : 'rgba(108,117,125,0.1)';
      // Flow set-point from the Fröling app: dashed, thinner, in the set-point colour. Built
      // from the same points as the actual line (index tooltips pair them by array index), with
      // null — a gap — where no set-point is known; none at all → no line.
      const setPointDs = (i: number, label: string, expanded: boolean): any[] => {
        const data = pts.filter(p => p.heatingCircuits?.[i]).map(p => {
          const target = p.heatingCircuits[i].flowTempTargetC ?? 0;
          return { x: p.ts, y: target > 0 ? target : null };
        });
        return !data.some(d => d.y != null) ? [] : [{
          linkKey: 'soll',
          label,
          data,
          borderColor: sollColor,
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: expanded ? 1.5 : 1,
          borderDash: [5, 4] as number[],
        }];
      };
      for (let i = 0; i < circuits.length; i++) {
        const key: string = circuits[i].name;
        const name = this.hcLabel(key);   // "Heizkreis 01" in the UI language
        const color = this.hcColor(i);
        const hcPts = pts.filter(p => p.heatingCircuits?.[i]);
        this.hcChartData[key] = {
          datasets: [
            {
              linkKey: 'ist',
              label: name,
              data: hcPts.map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
              borderColor: color,
              backgroundColor: 'transparent',
              fill: false,
            } as any,
            ...setPointDs(i, t('HEIZUNG_HK_SOLL_LINE', { name }), false),
          ],
        };
        this.hcExpandedData[key] = {
          datasets: [
            {
              linkKey: 'ist',
              label: t('HEIZUNG_FLOW_ACTUAL'),
              data: hcPts.map(p => ({ x: p.ts, y: p.heatingCircuits[i].flowTempActualC })),
              borderColor: color,
              backgroundColor: 'transparent',
              fill: false,
              borderWidth: 2,
            } as any,
            ...setPointDs(i, t('HEIZUNG_FLOW_TARGET'), true),
            {
              linkKey: 'pump',
              label: t('HEIZUNG_PUMPE'),
              data: hcPts.map(p => ({ x: p.ts, y: this.pumpToNumber(p.heatingCircuits[i].pumpState) })),
              borderColor: pumpColor,
              backgroundColor: pumpFill,
              fill: true,
              borderWidth: 1.5,
              stepped: 'before' as const,
              yAxisID: 'y1',
            } as any,
          ],
        };
        this.hcExpandedOptions[key] = buildDualAxisOptions(true, t('HEIZUNG_AXIS_FLOW'), '°C', t('HEIZUNG_PUMPE'), this.hcView(key));
      }
    }
  }

  /** Date / time locale of the UI language — uiLocale(), but from the TranslateService: on a language
   *  switch our onLangChange handler may run before AppComponent's sets <html lang>. */
  private get locale(): string {
    return this.translate.currentLang === 'de' ? 'de-DE' : 'en-GB';
  }

  /** Card: the last 7 calendar days (missing days = 0 kg), today drawn lighter as it is still growing. */
  private buildPelletBarChart(): void {
    const { textColor } = chartPalette(this.darkMode);
    if (this.pelletStats.length === 0) return;
    const byDate = new Map(this.pelletStats.map(s => [s.date, s]));
    const days: string[] = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      days.push(localIsoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - i)));
    }
    const todayIso = days[days.length - 1];
    const labels = days.map(d => new Date(d + 'T00:00:00')
      .toLocaleDateString(this.locale, { day: '2-digit', month: '2-digit' }));
    const values = days.map(d => byDate.get(d)?.consumptionKg ?? 0);
    const color = this.darkMode ? '#a1887f' : '#795548';
    const colorToday = this.darkMode ? 'rgba(161,136,127,0.45)' : 'rgba(121,85,72,0.4)';
    const nowLabel = today.toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit' });
    this.pelletChartData = {
      labels,
      datasets: [{
        label: 'kg',
        data: values,
        backgroundColor: days.map(d => d === todayIso ? colorToday : color),
        borderRadius: 3,
      }],
    };
    this.pelletChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const d = days[items[0]?.dataIndex ?? 0];
              return d === todayIso
                ? this.translate.instant('HEIZUNG_PELLET_HEUTE_BIS', { time: nowLabel })
                : labels[items[0]?.dataIndex ?? 0];
            },
            label: (item) => {
              const s = byDate.get(days[item.dataIndex]);
              const rate = s?.avgBurnRateKgH ? ` · Ø ${s.avgBurnRateKgH.toFixed(1)} kg/h` : '';
              const burn = s?.burnHours ? ` · ${s.burnHours.toFixed(1)} h` : '';
              return ` ${(item.parsed.y ?? 0).toFixed(1)} kg${burn}${rate}`;
            },
          },
        },
      },
      scales: {
        x: { display: true, ticks: { color: textColor, font: { size: 10 }, maxRotation: 0 }, grid: { display: false } },
        y: {
          ...yMinSpan(1),   // 10 × 0.1 kg (the tooltip's precision)
          display: true,
          beginAtZero: true,
          // whole kg only: an all-zero week would otherwise get 0 / 0.5 / 1 → "0 kg, 1 kg, 1 kg"
          ticks: { color: textColor, maxTicksLimit: 4, precision: 0, font: { size: 10 }, callback: (val) => `${(val as number).toFixed(0)} kg` },
          grid: { display: false },
        },
      },
    };
  }

  /**
   * Overlay: bars = kg per hour (Tag) or per day (longer ranges), line = burn rate in kg/h — the
   * mean of each 10-min bucket (Tag) or the mean while burning per day. Dashed / fainter = not
   * final yet (the running hour / today; the burn since the last 1 kg counter step is estimated
   * and only in the line).
   */
  private buildPelletUsageChart(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const short = this.range === 'day';
    const bucketMs = short ? HOUR_MS : DAY_MS;
    const lineBucketMs = short ? 10 * 60 * 1000 : DAY_MS;
    const { from: xMin, to: xMax } = this.pelletWindow();
    const t = (k: string, p?: any) => this.translate.instant(k, p);
    // the legend keeps "(kg/h)", the tooltip prints the unit with the value
    const stripUnit = (label: string) => (label ?? '').replace(/\s*\(kg\/h\)\s*$/, '');

    const barColor  = this.darkMode ? '#a1887f' : '#795548';
    const barFaded  = this.darkMode ? 'rgba(161,136,127,0.45)' : 'rgba(121,85,72,0.4)';
    // the burn rate in red, like its "🔥 … kg/h" figures — not the set-point orange
    const lineColor = this.darkMode ? '#ff6b6b' : '#dc3545';

    const bars = this.pelletBars;
    const line = this.pelletLine;
    const rateOf = (b: PelletUsageBucket) => short ? b.rateKgH : b.burnRateKgH;

    const kg = bars.reduce((s, b) => s + (b.kg ?? 0), 0);
    const burnMin = bars.reduce((s, b) => s + (b.burnMinutes ?? 0), 0);
    // Tag: the counter steps of the last 24 h (as on the card); the hourly bars can differ by a
    // fraction of a kg — the burn after a step is counted by the next one, the bars put it where it burnt
    this.pelletRangeKg = short && this.pelletUsage ? this.pelletUsage.last24hKg : bars.length ? kg : null;
    this.pelletRangeBurnH = bars.length ? burnMin / 60 : null;
    // rate while burning = the buckets' rates weighted by their burn time: kg without burn time
    // (legacy days before the archive, kg placed into a data gap) stay out of it
    let rateKg = 0, rateMin = 0;
    for (const b of bars) {
      const m = b.burnMinutes ?? 0;
      if (b.burnRateKgH != null && m > 0) { rateKg += b.burnRateKgH * m / 60; rateMin += m; }
    }
    this.pelletRangeAvgRate = rateMin >= 15 ? rateKg / (rateMin / 60) : null;

    const fmtTime = (ms: number) => new Date(ms).toLocaleTimeString(this.locale, { hour: '2-digit', minute: '2-digit' });
    const fmtDay  = (ms: number) => new Date(ms).toLocaleDateString(this.locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
    const bucketTitle = (start: number, len: number) => len >= DAY_MS ? fmtDay(start) : `${fmtTime(start)}–${fmtTime(start + len)}`;
    const barText = (b: PelletUsageBucket) => {
      const burn = b.burnMinutes ? ` · ${t('HEIZUNG_PELLET_BRENNDAUER')} ${b.burnMinutes >= 90
        ? (b.burnMinutes / 60).toFixed(1) + ' h' : Math.round(b.burnMinutes) + ' min'}` : '';
      const prov = b.provisionalKg ? ` · ${t('HEIZUNG_PELLET_GESCHAETZT', { kg: b.provisionalKg.toFixed(1) })}` : '';
      return `${(b.kg ?? 0).toFixed(1)} kg${burn}${prov}${b.gap ? ' · ' + t('HEIZUNG_PELLET_LUECKE') : ''}`;
    };
    const barAt = (x: number) => bars.find(b => x >= b.ts && x < b.ts + bucketMs);

    // Tag: bars (kg in an hour) and line (kg/h) are the same quantity — one shared scale, also
    // when zoomed (each axis would otherwise fit its own visible data)
    const sharedMax = (scale: any) => {
      if (!short) return;
      const ub = scale.chart?.scales?.x?.getUserBounds?.();
      const lo = ub?.minDefined ? ub.min : xMin, hi = ub?.maxDefined ? ub.max : xMax;
      let m = 7;
      for (const b of bars) {
        const cx = b.ts + bucketMs / 2;
        if (cx >= lo && cx <= hi) m = Math.max(m, b.kg ?? 0);
      }
      for (const b of line) {
        const cx = b.ts + lineBucketMs / 2, r = rateOf(b);
        if (r != null && cx >= lo && cx <= hi) m = Math.max(m, r);
      }
      scale.min = 0;
      scale.max = Math.ceil(m);
    };

    this.pelletUsageData = {
      datasets: [
        {
          type: 'bar',
          linkKey: 'kg',
          label: t(short ? 'HEIZUNG_PELLET_KG_STUNDE' : 'HEIZUNG_PELLET_KG_TAG'),
          data: bars.map(b => ({ x: b.ts + bucketMs / 2, y: b.kg })) as any,
          backgroundColor: bars.map(b => b.provisional ? barFaded : barColor),
          borderRadius: 3,
          barPercentage: 0.9,
          categoryPercentage: 1,
          yAxisID: 'y',
          order: 2,
        } as any,
        {
          type: 'line',
          linkKey: 'rate',
          label: t(short ? 'HEIZUNG_PELLET_RATE' : 'HEIZUNG_PELLET_RATE_BURNING'),
          data: line.map(b => ({ x: b.ts + lineBucketMs / 2, y: rateOf(b) })) as any,
          borderColor: lineColor,
          backgroundColor: lineColor,
          pointBackgroundColor: lineColor,
          pointBorderColor: lineColor,
          pointHoverBackgroundColor: lineColor,
          borderWidth: 2,
          pointRadius: short ? 0 : 2.5,
          pointHoverRadius: 4,
          cubicInterpolationMode: 'monotone',   // no overshoot above the real rate at the burn's edges
          spanGaps: false,
          // the not yet counted part (since the last 1 kg step / today) is dashed
          segment: { borderDash: (ctx: any) => line[ctx.p1DataIndex]?.provisional ? [4, 3] : undefined },
          yAxisID: 'y1',
          order: 1,
        } as any,
      ],
    };

    // phone (full-screen sheet): compact legend / tooltip, 10 px ticks, no axis titles (units in the ticks)
    const phone = this.phone;
    const tickFont = { size: phone ? 10 : 11 };
    this.pelletUsageOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'nearest', axis: 'x', intersect: false },
      layout: { padding: { top: 4, right: 2, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: true, labels: { color: textColor, usePointStyle: true, boxWidth: 8, font: { size: 11 }, ...(phone ? { boxHeight: 8, padding: 6 } : {}) } },
        tooltip: {
          ...(phone ? PHONE_TOOLTIP : {}),
          callbacks: {
            title: (items: any[]) => {
              const it = items[0];
              if (!it) return '';
              const isLine = it.dataset.yAxisID === 'y1';
              const len = isLine ? lineBucketMs : bucketMs;
              return bucketTitle(it.parsed.x - len / 2, len);
            },
            label: (item: any) => {
              const y = item.parsed.y;
              if (y == null) return '';
              if (item.dataset.yAxisID === 'y1') {
                return ` ${stripUnit(item.dataset.label)}: ${y.toFixed(1)} kg/h (${Math.round(y * 1000 / 60)} g/min)`;
              }
              const b = bars[item.dataIndex];
              return ` ${b ? barText(b) : y.toFixed(1) + ' kg'}`;
            },
            // Tag: the 10-min line almost always wins "nearest" — add the hour bar under the cursor
            afterBody: (items: any[]) => {
              if (!short || !items.length || items.some(i => i.dataset.yAxisID === 'y')) return [];
              const b = barAt(items[0].parsed.x);
              return b ? [`${bucketTitle(b.ts, bucketMs)}: ${barText(b)}`] : [];
            },
          },
        },
        zoom: dragZoom(this.zoomChanged),
        ...linkOnlyOptions(VIEWS.pellet).plugins,
      } as any,
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          offset: false,   // a bar chart's default offset would squeeze the time axis
          // ticks on whole local hours (Tag) / noons under the day bars / month starts, thinned by
          // autoSkip — from the live (possibly zoomed) range, not the full window
          afterBuildTicks: (scale: any) => { scale.ticks = alignedTimeTicks(scale.min, scale.max).map(value => ({ value })); },
          ticks: {
            color: textColor, font: tickFont, maxRotation: 0, autoSkip: true, autoSkipPadding: 12,
            callback: function (this: any, val: any) { return timeAxisTick(this.min, this.max)(val); },
          },
          grid: { display: false },
        },
        y: {
          ...yMinSpan(1),   // 10 × 0.1 kg
          position: 'left',
          beginAtZero: true,
          afterDataLimits: sharedMax,
          ticks: { color: textColor, maxTicksLimit: phone ? 5 : 6, font: tickFont, callback: (val: any) => `${+val} kg` },
          grid: { color: gridColor },
          title: { display: !phone, text: t(short ? 'HEIZUNG_PELLET_KG_STUNDE' : 'HEIZUNG_PELLET_KG_TAG'), color: textColor, font: { size: 11 } },
        },
        y1: {
          ...yMinSpan(1),   // 10 × 0.1 kg/h
          position: 'right',
          beginAtZero: true,
          suggestedMax: 7,
          afterDataLimits: sharedMax,
          ticks: { color: textColor, maxTicksLimit: phone ? 5 : 6, font: tickFont, callback: (val: any) => phone ? `${+val} kg/h` : `${+val}` },
          grid: { drawOnChartArea: false },
          title: { display: !phone, text: t('HEIZUNG_PELLET_RATE_AXIS'), color: textColor, font: { size: 11 } },
        },
      } as any,
    };
  }
}
